import React, { useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { ArrowRight, Lock, Users, Zap } from 'lucide-react';

export default function HeroSection({ onCTAClick }) {
  const statsRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in');
          }
        });
      },
      { threshold: 0.1 }
    );

    if (statsRef.current) {
      observer.observe(statsRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section className="relative pt-32 pb-20 px-4 overflow-hidden">
      {/* Hero Content */}
      <div className="container mx-auto max-w-6xl">
        <div className="text-center space-y-8">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass border border-primary/20">
            <Lock className="w-4 h-4 text-primary" />
            <span className="text-sm text-muted-foreground">Migration Auth0 Sécurisée</span>
          </div>

          {/* Main Heading */}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight">
            <span className="block mb-2">Migrez </span>
            <span className="text-gradient-purple">100% de vos utilisateurs</span>
            <span className="block mt-2">vers Auth0 sans aucune</span>
            <span className="block">action de leur part</span>
          </h1>

          {/* Subheading */}
          <p className="text-lg sm:text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            Une approche <span className="text-primary font-semibold">Commando</span> de l'identité : 
            Extraction, Transformation et Importation massive. Pas de changement de mot de passe, 
            pas de déconnexion, juste une transition invisible.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
            <Button 
              size="lg"
              onClick={onCTAClick}
              className="bg-primary hover:bg-primary/90 text-primary-foreground glow-purple text-base px-8 py-6 transition-smooth group"
            >
              Demander une étude de faisabilité
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button 
              size="lg"
              variant="outline"
              className="border-secondary text-secondary hover:bg-secondary/10 text-base px-8 py-6 transition-smooth"
              onClick={() => document.getElementById('protocol')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Voir le protocole
            </Button>
          </div>

          {/* Stats */}
          <div 
            ref={statsRef}
            className="grid grid-cols-1 sm:grid-cols-3 gap-6 pt-16 max-w-4xl mx-auto"
          >
            <div className="glass rounded-xl p-6 hover-lift">
              <div className="flex items-center justify-center mb-3">
                <Users className="w-8 h-8 text-primary" />
              </div>
              <div className="text-3xl font-bold text-gradient-purple mb-1">100%</div>
              <div className="text-sm text-muted-foreground">Migration complète</div>
            </div>
            
            <div className="glass rounded-xl p-6 hover-lift">
              <div className="flex items-center justify-center mb-3">
                <Zap className="w-8 h-8 text-secondary" />
              </div>
              <div className="text-3xl font-bold text-gradient-blue mb-1">0</div>
              <div className="text-sm text-muted-foreground">Action utilisateur</div>
            </div>
            
            <div className="glass rounded-xl p-6 hover-lift">
              <div className="flex items-center justify-center mb-3">
                <Lock className="w-8 h-8 text-primary" />
              </div>
              <div className="text-3xl font-bold text-gradient-purple mb-1">Invisible</div>
              <div className="text-sm text-muted-foreground">Transition fluide</div>
            </div>
          </div>
        </div>
      </div>

      {/* Decorative elements */}
      <div className="absolute top-20 left-10 w-64 h-64 bg-primary/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-20 right-10 w-64 h-64 bg-secondary/5 rounded-full blur-3xl pointer-events-none" />
    </section>
  );
}
